import { ref } from "firebase/storage";
import { useParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import MessageTimestamp from "./message-timestamp";
import { Candidate } from "@/types/candidate";
import { storage } from "@/config/firebaseConfig";
import { trimMessageToMaxLength } from "../utils";

interface CandidateListItemProps {
  candidate: Candidate;
  selectedAdId: string;
  className?: string;
  previewMessage?: any;
  selectedChats: string[];
  setSelectedChats: React.Dispatch<React.SetStateAction<string[]>>;
}

const CandidateListItem = ({
  candidate,
  previewMessage,
  selectedChats,
  setSelectedChats,
}: CandidateListItemProps) => {
  const { userId: selectedCandidate } = useParams<{
    userId?: string;
  }>();

  const [profileImageUrl, isLoadingProfileImage, profileImageError] =
    useDownloadURL(
      candidate.hasProfilePic || candidate.profilePictureUrl
        ? ref(storage, `mobileUsersFiles/${candidate.uid}/profile_image`)
        : null
    );

  if (!candidate) {
    return null;
  }

  const name = candidate.givenName;
  const firstName = name?.split(" ")[0];

  const onCheckedChange = (checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedChats((prev) => [...prev, candidate.uid]);
    } else {
      setSelectedChats((prev) => prev.filter((id) => id !== candidate.uid));
    }
  };

  const isUnread = previewMessage && previewMessage.readByBusiness === false;
  const isMarkedForReview =
    (candidate as any).reviewStatus === "marked_for_review";

  return (
    <div
      className={cn(
        "flex items-start gap-3.5 px-4 py-2 hover:bg-accent",
        selectedCandidate === candidate.uid && "bg-accent",
        isUnread && "bg-blue-50 dark:bg-blue-900 border-l-4 border-blue-500",
        isMarkedForReview &&
          "bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500"
      )}>
      <Checkbox
        id={candidate.uid}
        className="mt-2.5"
        checked={selectedChats.includes(candidate.uid)}
        onCheckedChange={onCheckedChange}
      />
      <Avatar className="h-10 w-10">
        {isLoadingProfileImage ? (
          <AvatarFallback>
            <Loader2 className="h-4 w-4 animate-spin" />
          </AvatarFallback>
        ) : profileImageError ? (
          <AvatarFallback className="bg-primary/10 text-primary">
            <span className="text-xl font-medium">
              {firstName?.[0]}
              {candidate.surname?.[0]}
            </span>
          </AvatarFallback>
        ) : profileImageUrl ? (
          <AvatarImage
            src={profileImageUrl}
            alt={`${firstName}'s avatar`}
            className="object-cover"
          />
        ) : (
          <AvatarFallback className="bg-primary/10 text-primary">
            <span className="text-xl font-medium">
              {firstName?.[0]}
              {candidate.surname?.[0]}
            </span>
          </AvatarFallback>
        )}
      </Avatar>
      <div className="flex-1 h-10 space-y-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={cn("font-medium", isUnread && "font-bold")}>
              {firstName} {candidate.surname}
            </span>
          </div>
          {previewMessage && (
            <span className="text-xs text-muted-foreground">
              <MessageTimestamp timestamp={previewMessage?.timestamp} />
            </span>
          )}
        </div>
        {/* <p className="text-sm font-medium">
          {age} år, {candidate.latestOccupation}
        </p> */}
        <div className="flex items-center justify-between">
          <p
            className={cn(
              "text-xs text-muted-foreground line-clamp-2",
              isUnread && "font-semibold text-foreground"
            )}>
            {trimMessageToMaxLength(previewMessage?.message, 40)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CandidateListItem;
